import { useEffect, useMemo, useState } from 'react';
import { fetchJson } from '../api.js';
import { useSortable, SortableTh } from '../utils/sort.jsx';
import DataTable from './datatable/DataTable.jsx';

const fmt = (n) => (n ?? 0).toLocaleString();
const money = (n) => '$' + Math.round(n ?? 0).toLocaleString();
const pct = (n) => (n == null ? '—' : (n * 100).toFixed(1) + '%');

const TIER_LABELS = {
  small: 'Small (<10m³)', regular: 'Regular (10m³)',
  oversize18: 'Oversize 18m³', oversize28: 'Oversize 28m³'
};

function buildUrl(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== false && v !== '') {
      params.set(k, v === true ? '1' : String(v));
    }
  });
  return '/api/drilldown?' + params.toString();
}

// One headline KPI in the Overall row
function Kpi({ label, value }) {
  return (
    <div className="dd-kpi">
      <div className="lbl">{label}</div>
      <div className="val">{value}</div>
    </div>
  );
}

export default function DrillDown({ title, query, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    fetchJson(buildUrl(query))
      .then(setData)
      .catch(e => setError(e.message));
  }, [JSON.stringify(query)]);

  // Sort/filter helpers used by the customers table
  const totals = useMemo(() => {
    if (!data) return { loads: 0, revenue: 0 };
    return { loads: data.summary.tips || 0, revenue: data.summary.revenue || 0 };
  }, [data]);

  const recordsSort = useSortable(data && data.records, 'date', 'desc', (r, k) => {
    if (k === 'date') return r.date;
    if (k === 'time') return r.arrivalTime;
    if (k === 'status') return r.status;
    if (k === 'plate') return r.plate || '';
    if (k === 'customer') return r.customer || '';
    if (k === 'tier') return r.tier || '';
    if (k === 'tankSizeM3') return r.tankSizeM3 == null ? -Infinity : r.tankSizeM3;
    if (k === 'rate') return r.rate == null ? -Infinity : r.rate;
    return r[k];
  });
  const topCustSort = useSortable(data && data.breakdowns && data.breakdowns.topCustomers, 'revenue', 'desc');
  const byTierSort  = useSortable(data && data.breakdowns && data.breakdowns.byTier, 'revenue', 'desc');
  const byRateSort  = useSortable(data && data.breakdowns && data.breakdowns.byRate, 'rate', 'asc');

  return (
    <div className="dd-backdrop" onClick={onClose}>
      <div className="dd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dd-header">
          <div>
            <h3 style={{margin:0}}>{title || 'Detail'}</h3>
            {data && (
              <div className="small" style={{marginTop:4}}>
                {data.period.start} → {data.period.end}
                {query.customer && <> · customer: <strong>{query.customer}</strong></>}
                {query.tier && <> · tier: <strong>{TIER_LABELS[query.tier] || query.tier}</strong></>}
                {query.rate && <> · regular rate: <strong>${query.rate}</strong></>}
                {query.q && <> · Q{query.q}</>}
              </div>
            )}
          </div>
          <button className="refresh-btn" onClick={onClose}>Close</button>
        </div>

        {!data && !error && <div className="loading">Loading…</div>}
        {error && <div className="error">Error: {error}</div>}

        {data && (
          <div className="dd-body">

            {/* OVERALL — six headline numbers in a clean row */}
            <div className="dd-section">
              <div className="dd-kpi-row">
                <Kpi label="Loads"          value={fmt(data.summary.tips)} />
                <Kpi label="Revenue"        value={money(data.summary.revenue)} />
                <Kpi label="Volume m³"      value={fmt(data.summary.volumeM3)} />
                <Kpi label="Operating days" value={fmt(data.summary.workedDays)} />
                <Kpi label="Customers"      value={fmt(data.summary.customers)} />
                <Kpi label="Avg rate"       value={money(data.summary.avgRate)} />
              </div>
            </div>

            {/* TWO-COLUMN LAYOUT: customers (left) | tier + rate (right) */}
            <div className="dd-two-col">

              {/* TOP 10 CUSTOMERS */}
              {data.breakdowns?.topCustomers?.length > 0 && (
                <div className="dd-section dd-block">
                  <div className="dd-section-label">Top customers (by revenue)</div>
                  <table className="dd-table">
                    <thead>
                      <tr>
                        <th style={{width:26}}>#</th>
                        <SortableTh k="customer" sort={topCustSort.sort} toggle={topCustSort.toggle}>Customer</SortableTh>
                        <SortableTh k="loads" sort={topCustSort.sort} toggle={topCustSort.toggle} className="num">Loads</SortableTh>
                        <SortableTh k="revenue" sort={topCustSort.sort} toggle={topCustSort.toggle} className="num">Revenue</SortableTh>
                        <th className="num">% rev</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCustSort.sorted.map((c, i) => (
                        <tr key={c.customer}>
                          <td className="rank">{i + 1}</td>
                          <td title={c.customer}>{c.customer}</td>
                          <td className="num">{fmt(c.loads)}</td>
                          <td className="num"><strong>{money(c.revenue)}</strong></td>
                          <td className="num">{totals.revenue > 0 ? (c.revenue / totals.revenue * 100).toFixed(1) + '%' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="dd-stack">
                {/* BY TRUCK SIZE */}
                {data.breakdowns?.byTier?.length > 0 && (
                  <div className="dd-section dd-block">
                    <div className="dd-section-label">By truck size</div>
                    <table className="dd-table">
                      <thead>
                        <tr>
                          <SortableTh k="label" sort={byTierSort.sort} toggle={byTierSort.toggle}>Tier</SortableTh>
                          <SortableTh k="loads" sort={byTierSort.sort} toggle={byTierSort.toggle} className="num">Loads</SortableTh>
                          <SortableTh k="revenue" sort={byTierSort.sort} toggle={byTierSort.toggle} className="num">Revenue</SortableTh>
                          <th className="num">% rev</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byTierSort.sorted.map((t) => (
                          <tr key={t.tier}>
                            <td>{t.label}</td>
                            <td className="num">{fmt(t.loads)}</td>
                            <td className="num">{money(t.revenue)}</td>
                            <td className="num">{totals.revenue > 0 ? (t.revenue / totals.revenue * 100).toFixed(1) + '%' : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* BY CHARGE-OUT RATE */}
                {data.breakdowns?.byRate?.length > 0 && (
                  <div className="dd-section dd-block">
                    <div className="dd-section-label">By charge-out rate</div>
                    <table className="dd-table">
                      <thead>
                        <tr>
                          <SortableTh k="rate" sort={byRateSort.sort} toggle={byRateSort.toggle}>Rate</SortableTh>
                          <SortableTh k="loads" sort={byRateSort.sort} toggle={byRateSort.toggle} className="num">Loads</SortableTh>
                          <SortableTh k="revenue" sort={byRateSort.sort} toggle={byRateSort.toggle} className="num">Revenue</SortableTh>
                          <th className="num">% rev</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byRateSort.sorted.map((r) => (
                          <tr key={r.rate}>
                            <td>${r.rate} / load</td>
                            <td className="num">{fmt(r.loads)}</td>
                            <td className="num">{money(r.revenue)}</td>
                            <td className="num">{totals.revenue > 0 ? (r.revenue / totals.revenue * 100).toFixed(1) + '%' : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* RAW RECORDS */}
            <div className="dd-section dd-block">
              <div className="dd-section-label">
                Loads ({fmt(data.totalCount)}{data.truncated ? ', showing first ' + fmt(data.records.length) : ''})
                {data.summary && data.summary.pendingCount > 0 && (
                  <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: '0.75em', fontWeight: 600 }}>
                    {fmt(data.summary.pendingCount)} pending · {money(data.summary.pendingRevenue)} not-yet-approved revenue
                  </span>
                )}
              </div>
              <DataTable
                viewName="drilldown.records"
                data={data.records || []}
                emptyMessage="No records in this period."
                defaultSort={[{ id: 'date', desc: true }]}
                columns={[
                  { id: 'date',       accessorKey: 'date',         header: 'Date',     size: 100, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' } },
                  { id: 'time',       accessorKey: 'arrivalTime',  header: 'Time',     size:  90, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' }, cell: ({ getValue }) => getValue() || '—' },
                  { id: 'status',     accessorKey: 'status',       header: 'Status',   size: 100, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' }, cell: ({ row }) => row.original.status === 'pending'
                    ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 999, fontSize: '0.75em', fontWeight: 600 }}>Pending</span>
                    : <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: '0.75em', fontWeight: 600 }}>Approved</span> },
                  { id: 'plate',      accessorKey: 'plate',        header: 'Plate',    size: 100, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' }, cell: ({ getValue }) => getValue() || '—' },
                  { id: 'customer',   accessorKey: 'customer',     header: 'Customer', size: 220, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' } },
                  { id: 'tier',       accessorKey: 'tier',         header: 'Tier',     size: 140, meta: { rowBg: r => r.status === 'pending' ? '#fefce8' : '' }, cell: ({ getValue }) => TIER_LABELS[getValue()] || getValue() || '?' },
                  { id: 'tankSizeM3', accessorKey: 'tankSizeM3',   header: 'Tank m³',  size:  90, meta: { align: 'right', format: 'num', rowBg: r => r.status === 'pending' ? '#fefce8' : '' } },
                  { id: 'rate',       accessorKey: 'rate',         header: 'Rate',     size: 100, meta: { align: 'right', format: 'money', rowBg: r => r.status === 'pending' ? '#fefce8' : '' } },
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Wrapper to make any value clickable and open a drill-down
export function ClickableValue({ query, title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span className="clickable" onClick={() => setOpen(true)} role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(true)}>
        {children}
      </span>
      {open && <DrillDown title={title} query={query} onClose={() => setOpen(false)} />}
    </>
  );
}
